import { useState, type CSSProperties, type FC, type FormEvent } from 'react';
import type { ClientJointArticle } from '../types';

interface ClientArticlesOverlayProps {
  articles: ClientJointArticle[];
  isOpen: boolean;
  onClose: () => void;
  onCreateArticle: (payload: Partial<ClientJointArticle>) => Promise<void>;
  onUpdateArticle: (id: string, payload: Partial<ClientJointArticle>) => Promise<void>;
}

function articleStatusLabel(status: string): string {
  switch (status) {
    case 'COLLABORATION':
      return 'Соавторство';
    case 'REVIEW':
      return 'Рецензирование';
    case 'SUBMITTED':
      return 'Подана';
    case 'PUBLISHED':
      return 'Опубликована';
    default:
      return status;
  }
}

/** Joint articles — cab-overlay chrome + cabinet-* body. */
export const ClientArticlesOverlay: FC<ClientArticlesOverlayProps> = ({
  articles,
  isOpen,
  onClose,
  onCreateArticle,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [fieldOfScience] = useState('Образовательные технологии');
  const [targetJournal, setTargetJournal] = useState('Computers & Education');
  const [quartile, setQuartile] = useState('Q1');
  const [coauthors, setCoauthors] = useState('Ибраев А., Клиент А.');
  const [manuscriptUrl, setManuscriptUrl] = useState('');
  const [deadline, setDeadline] = useState('2026-11-30');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setIsSubmitting(true);
      await onCreateArticle({
        title: title.trim(),
        field_of_science: fieldOfScience,
        target_journal: targetJournal,
        quartile,
        status: 'COLLABORATION',
        progress_percent: 25,
        coauthors,
        manuscript_url: manuscriptUrl,
        deadline,
      });
      setTitle('');
      setIsAdding(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true" aria-labelledby="articles-title">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Публикации клиента</small>
              <h2 id="articles-title">Совместные статьи и соавторство</h2>
              <p>Публикационные проекты в коллаборации с учёными Академии и партнёрами</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button
              type="button"
              className="cab-btn primary"
              onClick={() => setIsAdding(true)}
            >
              + Добавить публикацию
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cab-body">
          {articles.length === 0 ? (
            <div className="cabinet-note">Совместных публикаций пока нет</div>
          ) : (
            <div className="cabinet-grid2">
              {articles.map((art) => (
                <article
                  key={art.id}
                  className="cabinet-card"
                  style={{ '--accent': '#e2ad3a' } as CSSProperties}
                >
                  <div className="cabinet-actions" style={{ marginBottom: 8 }}>
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': '#e2ad3a' } as CSSProperties}
                    >
                      {art.article_code}
                    </span>
                    {art.quartile ? (
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': '#49ddb7' } as CSSProperties}
                      >
                        {art.quartile}
                      </span>
                    ) : null}
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': '#8faebb' } as CSSProperties}
                    >
                      {articleStatusLabel(art.status)}
                    </span>
                    <span className="cabinet-spacer" />
                    <span style={{ fontSize: 13, color: '#e2ad3a', fontWeight: 700 }}>
                      {art.progress_percent}%
                    </span>
                  </div>
                  <strong>{art.title}</strong>
                  <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                    Целевой журнал: {art.target_journal}
                  </p>
                  <div className="cabinet-actions" style={{ marginTop: 10 }}>
                    <span style={{ fontSize: 11, color: '#8faebb' }}>
                      Соавторы: {art.coauthors || '—'}
                    </span>
                    <span className="cabinet-spacer" />
                    {art.deadline ? (
                      <span style={{ fontSize: 11, color: '#8faebb' }}>
                        Срок сдачи: {art.deadline}
                      </span>
                    ) : null}
                    {art.manuscript_url ? (
                      <a
                        href={art.manuscript_url}
                        target="_blank"
                        rel="noreferrer"
                        className="cabinet-btn"
                      >
                        Открыть рукопись
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {isAdding ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 640 }}>
            <header className="cabinet-modal-head">
              <div>
                <h2>Новая совместная статья</h2>
                <p>Карточка соавторской публикации</p>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsAdding(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </header>
            <form className="cabinet-modal-body" onSubmit={handleSubmit}>
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Название статьи</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Название статьи на русском или английском"
                  />
                </label>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Целевой журнал</span>
                    <input
                      className="cabinet-input"
                      type="text"
                      value={targetJournal}
                      onChange={(e) => setTargetJournal(e.target.value)}
                      placeholder="Журнал Scopus / WoS"
                    />
                  </label>
                  <label className="cabinet-field">
                    <span>Квартиль</span>
                    <select
                      className="cabinet-select"
                      value={quartile}
                      onChange={(e) => setQuartile(e.target.value)}
                    >
                      <option value="Q1">Квартиль Q1</option>
                      <option value="Q2">Квартиль Q2</option>
                      <option value="Q3">Квартиль Q3</option>
                      <option value="Q4">Квартиль Q4</option>
                      <option value="КОКНВО">КОКНВО</option>
                    </select>
                  </label>
                </div>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Соавторы</span>
                    <input
                      className="cabinet-input"
                      type="text"
                      value={coauthors}
                      onChange={(e) => setCoauthors(e.target.value)}
                      placeholder="Соавторы через запятую"
                    />
                  </label>
                  <label className="cabinet-field">
                    <span>Срок</span>
                    <input
                      className="cabinet-input"
                      type="date"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                    />
                  </label>
                </div>
                <label className="cabinet-field">
                  <span>Ссылка на рукопись</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={manuscriptUrl}
                    onChange={(e) => setManuscriptUrl(e.target.value)}
                    placeholder="Ссылка на онлайн-рукопись"
                  />
                </label>
                <div className="cabinet-actions" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setIsAdding(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="cabinet-btn primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Создание…' : 'Создать'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};
